import { Link } from 'react-router-dom';
import SEO from '@/components/base/SEO';
import ServicePageLayout from '@/components/base/ServicePageLayout';

const breadcrumb = [
  { label: 'Accueil', to: '/' },
  { label: 'Dépannage menuiseries Juvignac' },
];

const jsonLdService = {
  '@context': 'https://schema.org',
  '@type': 'Service',
  name: 'Dépannage et réparation de menuiseries à Juvignac',
  provider: { '@id': 'https://heraultroulettesbaiesvitrees.fr/#business' },
  areaServed: { '@type': 'City', name: 'Juvignac' },
  description: 'Réparation de baies coulissantes, volets roulants, vitrages, stores et fenêtres à Juvignac. Intervention 24-48h, 7j/7. Devis gratuit, garantie 10 ans.',
  offers: { '@type': 'Offer', price: '0', priceCurrency: 'EUR', description: 'Devis gratuit' },
};

const jsonLdFAQ = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'Intervenez-vous dans les résidences récentes de Juvignac ?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Oui, Juvignac compte de nombreuses résidences récentes autour du golf de Fontcaude. Nous intervenons sur les baies coulissantes, stores et volets roulants électriques de ces constructions. Délai 24-48h, devis gratuit.',
      },
    },
    {
      '@type': 'Question',
      name: 'Réparez-vous les stores et BSO à Juvignac ?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Absolument. Les stores bannes et BSO sont très présents à Juvignac. Nous réparons toiles, bras, moteurs et assurons la motorisation. Diagnostic gratuit, intervention sous 24 à 48h.',
      },
    },
  ],
};

export default function JuvignacPage() {
  return (
    <>
      <SEO
        title="Réparation baie coulissante Juvignac | DFPRO"
        description="Baie coulissante, store ou volet roulant en panne à Juvignac ? DFPRO intervient 24-48h, 7j/7. Devis gratuit, garantie 10 ans."
        canonicalUrl="https://heraultroulettesbaiesvitrees.fr/depannage-menuiserie-juvignac/"
      />
      <ServicePageLayout
        heroImage="/images/ville-herault-01.jpg"
        title="Réparation de menuiseries et baie coulissante à Juvignac"
        subtitle="Résidentiel récent, stores et baies coulissantes : nos techniciens réparent vos menuiseries à Juvignac. Devis gratuit, intervention 24-48h, 7j/7."
        breadcrumb={breadcrumb}
        jsonLdService={jsonLdService}
        jsonLdFAQ={jsonLdFAQ}
        ctaTitle="Store ou baie en panne à Juvignac ?"
        ctaText="Devis gratuit, intervention 24-48h, garantie 10 ans. Nos techniciens se déplacent à Juvignac et dans l'ouest montpelliérain, 7j/7."
        ctaServiceLabel="Tous nos services"
      >
        <p className="text-lg text-secondary-500 leading-relaxed">
          Juvignac, à l'ouest de Montpellier, concentre un habitat récent avec de nombreuses baies coulissantes, stores et volets électriques. Nous intervenons pour tout type de panne. Appelez le <a href="tel:+33980802242" className="text-accent-500 font-semibold hover:underline">09 80 80 22 42</a>.
        </p>

        <div className="not-prose my-10 grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
          <div className="w-full h-[280px] md:h-[340px] rounded-lg overflow-hidden">
            <img
              src="/images/juvignac-baie-01.webp"
              alt="Réparation de baie vitrée coulissante à Juvignac"
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
          <div>
            <h3 className="text-xl font-heading font-bold text-primary-500 mb-3">Baie coulissante dans les résidences récentes</h3>
            <p className="text-secondary-500 mb-3 leading-relaxed">
              Les villas de Juvignac sont souvent équipées de grandes baies coulissantes. Après quelques années, les roulettes fatiguent et la baie <Link to="/baie-coulissante-qui-coince-herault/" className="text-accent-500 font-semibold hover:underline">devient dure à manœuvrer</Link>. Intervenir tôt évite d'endommager le rail.
            </p>
            <p className="text-secondary-500 mb-4 leading-relaxed">
              <Link to="/changer-roulettes-galets-baie-coulissante-herault/" className="text-accent-500 font-semibold hover:underline">Roulettes</Link>, <Link to="/reparation-rail-baie-coulissante-herault/" className="text-accent-500 font-semibold hover:underline">rail</Link>, <Link to="/reglage-baie-coulissante-herault/" className="text-accent-500 font-semibold hover:underline">réglage</Link>, <Link to="/remplacement-baie-coulissante-herault/" className="text-accent-500 font-semibold hover:underline">remplacement</Link>.
            </p>
            <Link to="/reparation-baie-coulissante-herault/" className="inline-flex items-center gap-2 text-accent-500 font-semibold hover:underline">
              Dépannage baie coulissante <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>

        <div className="not-prose my-10 grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
          <div>
            <h3 className="text-xl font-heading font-bold text-primary-500 mb-3">Store banne et BSO : pannes fréquentes</h3>
            <p className="text-secondary-500 mb-3 leading-relaxed">
              Avec l'ensoleillement généreux, les <Link to="/reparation-store-banne-herault/" className="text-accent-500 font-semibold hover:underline">stores bannes</Link> et <Link to="/reparation-bso-herault/" className="text-accent-500 font-semibold hover:underline">BSO</Link> sont très sollicités à Juvignac. Toile déchirée, bras plié, <Link to="/store-banne-bloque-herault/" className="text-accent-500 font-semibold hover:underline">store bloqué</Link>, <Link to="/motorisation-store-herault/" className="text-accent-500 font-semibold hover:underline">moteur</Link> en panne.
            </p>
            <p className="text-secondary-500 mb-4 leading-relaxed">
              Côté <Link to="/reparation-volet-roulant-herault/" className="text-accent-500 font-semibold hover:underline">volets roulants</Link> : <Link to="/volet-roulant-electrique-herault/" className="text-accent-500 font-semibold hover:underline">électriques</Link>, <Link to="/reparation-moteur-volet-roulant-herault/" className="text-accent-500 font-semibold hover:underline">moteur</Link>, <Link to="/motorisation-volet-roulant-herault/" className="text-accent-500 font-semibold hover:underline">motorisation</Link>.
            </p>
            <Link to="/reparation-store-herault/" className="inline-flex items-center gap-2 text-accent-500 font-semibold hover:underline">
              Dépannage store <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
          <div className="w-full h-[280px] md:h-[340px] rounded-lg overflow-hidden">
            <img
              src="/images/juvignac-volet-02.jpg"
              alt="Réparation de store et volet à Juvignac"
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
        </div>

        <div className="not-prose my-10 grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
          <div className="w-full h-[280px] md:h-[340px] rounded-lg overflow-hidden">
            <img
              src="/images/juvignac-vitrage-03.webp"
              alt="Remplacement de vitrage à Juvignac"
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
          <div>
            <h3 className="text-xl font-heading font-bold text-primary-500 mb-3">Vitrier et fenêtre</h3>
            <p className="text-secondary-500 mb-3 leading-relaxed">
              <Link to="/vitrier-herault/" className="text-accent-500 font-semibold hover:underline">Vitrier</Link> : <Link to="/remplacement-vitre-cassee-herault/" className="text-accent-500 font-semibold hover:underline">vitre cassée</Link>, <Link to="/remplacement-double-vitrage-herault/" className="text-accent-500 font-semibold hover:underline">double vitrage</Link>, <Link to="/double-vitrage-embue-herault/" className="text-accent-500 font-semibold hover:underline">vitrage embué</Link>. <Link to="/vitrier-urgence-herault/" className="text-accent-500 font-semibold hover:underline">Urgence</Link> 7j/7.
            </p>
            <p className="text-secondary-500 mb-4 leading-relaxed">
              <Link to="/reparation-fenetre-herault/" className="text-accent-500 font-semibold hover:underline">Fenêtres</Link> : <Link to="/changer-cremone-fenetre-herault/" className="text-accent-500 font-semibold hover:underline">crémone</Link>, <Link to="/changer-joint-fenetre-herault/" className="text-accent-500 font-semibold hover:underline">joints</Link>, <Link to="/reglage-oscillo-battant-herault/" className="text-accent-500 font-semibold hover:underline">oscillo-battant</Link>.
            </p>
            <Link to="/vitrier-herault/" className="inline-flex items-center gap-2 text-accent-500 font-semibold hover:underline">
              Service vitrier <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>

        <h2>Intervention à Juvignac sous 24 à 48h</h2>
        <p>
          Nous couvrons tout Juvignac : golf de Fontcaude, quartiers résidentiels, zones d'activités. Communes voisines : <Link to="/depannage-menuiserie-montpellier/" className="text-accent-500 font-semibold hover:underline">Montpellier</Link>, <Link to="/depannage-menuiserie-saint-jean-de-vedas/" className="text-accent-500 font-semibold hover:underline">Saint-Jean-de-Védas</Link>, Grabels. <Link to="/" className="text-accent-500 font-semibold hover:underline">Accueil DFPRO</Link>.
        </p>
      </ServicePageLayout>
    </>
  );
}